import * as React from 'react';
import {
  Button,
  Collapse,
  ListGroup,
  ListGroupItem,
  Table,
  TabPane,
} from 'reactstrap';
import moment from 'moment';
import { DECOMMISSIONING_TAB } from './AccountForm';
import { getDAMApp } from '../../services/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { logger } from '../../logging';

const IFB_HOSTING_DOMAIN_URL = process.env.FIREBASE_IFB_DOMAIN_URL;

export const DecommissioningStatusTab = ({
  account,
  system,
}: {
  account: any;
  system: string;
}) => {
  const fields = [
    {
      name: 'V1 Forms',
      field: 'decommissioned_v1_webforms_status',
    },
    {
      name: 'Workflows',
      field: 'decommissioned_workflows_status',
    },
    {
      name: 'Credentials',
      field: 'decommissioned_credentials_status',
    },
    {
      name: 'Marketing Lists',
      field: 'decommissioned_marketing_lists_status',
    },
  ];

  const [formsOpen, setFormsOpen] = React.useState(false);
  const [workflowsOpen, setWorkflowsOpen] = React.useState(false);
  const [retrying, setRetrying] = React.useState<Record<string, boolean>>({});

  const toggleForms = () => setFormsOpen(!formsOpen);
  const toggleWorkflows = () => setWorkflowsOpen(!workflowsOpen);

  const isDotdigital = system === 'dotdigital';

  const handleRetry = async (field: string) => {
    setRetrying((prev) => ({ ...prev, [field]: true }));
    try {
      const damApp = getDAMApp();
      const functions = getFunctions(damApp);
      const callable = httpsCallable(
        functions,
        'accountManager-callableRetryDecommissioningEntity'
      );

      await callable({
        accountId: account.id,
        system,
        entityType: field,
      });
    } catch (e) {
      logger.error(e);
    } finally {
      setRetrying((prev) => ({ ...prev, [field]: false }));
    }
  };

  const rows = fields.map((f) => {
    const value = account?.[f.field];
    const decommissionedForms = value?.decommissioned_forms || [];
    const decommissionedWorkflows = value?.decommissioned_workflows || [];

    // Cloud functions have a max timeout of 9 mins. If pending > 15 mins (900s), it crashed (OOM/Timeout).
    const isStuckPending =
      value?.status === 'pending' &&
      value?.last_execution_at &&
      moment().unix() - value.last_execution_at > 900;

    return (
      <React.Fragment key={f.field}>
        <tr>
          <td>
            {f.name}
            {isDotdigital &&
              f.field === 'decommissioned_v1_webforms_status' &&
              decommissionedForms.length > 0 && (
                <Button
                  color="link"
                  onClick={toggleForms}
                  size="sm"
                  style={{ marginLeft: '10px' }}
                >
                  ({decommissionedForms.length} forms){' '}
                  {formsOpen ? 'Hide' : 'Show'}
                </Button>
              )}
            {isDotdigital &&
              f.field === 'decommissioned_workflows_status' &&
              decommissionedWorkflows.length > 0 && (
                <Button
                  color="link"
                  onClick={toggleWorkflows}
                  size="sm"
                  style={{ marginLeft: '10px' }}
                >
                  ({decommissionedWorkflows.length} workflows){' '}
                  {workflowsOpen ? 'Hide' : 'Show'}
                </Button>
              )}
          </td>
          <td>
            {isStuckPending ? 'crashed (timeout/OOM)' : value?.status}
            {(value?.status === 'error' ||
              value?.status === 'failed' ||
              isStuckPending) &&
              value?.last_execution_result !== 'retrying' && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => handleRetry(f.field)}
                  disabled={retrying[f.field]}
                  style={{ marginLeft: '10px' }}
                >
                  {retrying[f.field] ? 'Retrying...' : 'Retry'}
                </Button>
              )}
          </td>
          <td>{value?.error}</td>
          <td>{value?.last_execution_result}</td>
          <td>
            {value?.last_execution_at
              ? moment.unix(value.last_execution_at).format()
              : null}
          </td>
        </tr>
        {isDotdigital && f.field === 'decommissioned_v1_webforms_status' && (
          <tr>
            <td colSpan={5}>
              <Collapse isOpen={formsOpen}>
                <ListGroup>
                  {decommissionedForms.map((form: any) => (
                    <ListGroupItem key={form.id}>
                      <strong>ID:</strong> {form.id}, <strong>Type:</strong>{' '}
                      {form.formType}, <strong>Title:</strong> {form.title},{' '}
                      <strong>URL:</strong>{' '}
                      <a
                        href={`${IFB_HOSTING_DOMAIN_URL}/builder/form/${form.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {`${IFB_HOSTING_DOMAIN_URL}/builder/form/${form.id}`}
                      </a>
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </Collapse>
            </td>
          </tr>
        )}
        {isDotdigital && f.field === 'decommissioned_workflows_status' && (
          <tr>
            <td colSpan={5}>
              <Collapse isOpen={workflowsOpen}>
                <ListGroup>
                  {decommissionedWorkflows.map((workflow: any) => (
                    <ListGroupItem key={workflow.id}>
                      <strong>ID:</strong> {workflow.id}, <strong>Name:</strong>{' '}
                      {workflow.name}
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </Collapse>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  });

  return (
    <TabPane tabId={DECOMMISSIONING_TAB}>
      <Table striped={false} size="sm">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Error Message</th>
            <th>Last Execution Result</th>
            <th>Last Execution</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>
    </TabPane>
  );
};
